import React, { useEffect } from 'react';
import { Navigate, Route, Routes, useSearchParams } from 'react-router-dom';
import { Box } from '@mui/material';
import { useMsal, AuthenticatedTemplate, UnauthenticatedTemplate } from '@azure/msal-react';
import { getLoginRequest, logoutRequest } from './authConfig';
import { getPortalBaseUrl, redirectTo } from './lib/runtimeUrls';
import { getPortalSelectUrl } from './lib/selectedCustomer';
import { getDisplayCustomerName } from './lib/customerDisplay';
import { setDocumentTitle } from './lib/documentTitle';
import { BlaalysModeProvider } from './context/BlaalysModeContext';
import BlaalysModeBanner from './components/BlaalysModeBanner';
import Navbar from './components/Navbar';
import HomePage from './pages/HomePage';
import ResidentsPage from './pages/ResidentsPage';
import LocksPage from './pages/LocksPage';
import AssignmentsPage from './pages/AssignmentsPage';
import OrdersPage from './pages/OrdersPage';
import PhonesPage from './pages/PhonesPage';
import AccessGroupsPage from './pages/AccessGroupsPage';
import ReportsPage from './pages/ReportsPage';
import HelpPage from './pages/HelpPage';
import { isOrdersEnabled } from './config/features';
import LoginPage from './pages/LoginPage';
import CustomerLandingPage from './pages/CustomerLandingPage';
import AccessDeniedPage from './pages/AccessDeniedPage';

const LogsToReportsRedirect = () => {
  const [searchParams] = useSearchParams();
  const next = new URLSearchParams(searchParams);
  next.set('report', 'hendelseslogg');
  return <Navigate to={`/reports?${next.toString()}`} replace />;
};

const PortalSelectRedirect = () => {
  useEffect(() => {
    const portalOrigin = new URL(getPortalBaseUrl()).origin;

    if (window.location.origin === portalOrigin) {
      return;
    }

    redirectTo(getPortalSelectUrl());
  }, []);

  return null;
};

const CustomerApp = () => {
  const { instance, accounts } = useMsal();

  useEffect(() => {
    const name = getDisplayCustomerName();
    setDocumentTitle(name === 'din kommune' ? null : name);
  }, []);

  const handleLogin = async () => {
    try {
      await instance.loginRedirect(getLoginRequest());
    } catch (error) {
      console.error('Login failed:', error);
    }
  };

  const handleLogout = async () => {
    try {
      const account =
        instance.getActiveAccount() ?? (accounts.length > 0 ? accounts[0] : null);

      await instance.logoutRedirect({
        ...logoutRequest,
        account: account ?? undefined,
        onRedirectNavigate: () => false
      });

      window.location.replace(logoutRequest.postLogoutRedirectUri);
    } catch (error) {
      console.error('Logout failed:', error);
      window.location.replace(logoutRequest.postLogoutRedirectUri);
    }
  };

  const user =
    accounts.length > 0
      ? {
          name: accounts[0].name || accounts[0].username || 'User',
          email: accounts[0].username || '',
          roles: ['Staff']
        }
      : null;

  return (
    <Box className="app-container">
      <AuthenticatedTemplate>
        <BlaalysModeProvider>
          <Navbar user={user} onLogout={handleLogout} />
          <BlaalysModeBanner />
          <Box className="main-content">
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/residents" element={<ResidentsPage />} />
              <Route path="/locks" element={<LocksPage />} />
              <Route path="/assignments" element={<AssignmentsPage />} />
              {isOrdersEnabled() ? (
                <Route path="/orders" element={<OrdersPage />} />
              ) : (
                <Route path="/orders" element={<Navigate to="/" replace />} />
              )}
              <Route path="/phones" element={<PhonesPage />} />
              <Route path="/access-groups" element={<AccessGroupsPage />} />
              <Route path="/logs" element={<LogsToReportsRedirect />} />
              <Route path="/reports" element={<ReportsPage />} />
              <Route path="/hjelp" element={<HelpPage />} />
              <Route path="/ingen-tilgang" element={<AccessDeniedPage />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Box>
        </BlaalysModeProvider>
      </AuthenticatedTemplate>

      <UnauthenticatedTemplate>
        <Routes>
          <Route path="/" element={<CustomerLandingPage onLogin={handleLogin} />} />
          <Route path="/login" element={<LoginPage onLogin={handleLogin} />} />
          <Route path="/velg-kommune" element={<PortalSelectRedirect />} />
          <Route path="/ingen-tilgang" element={<AccessDeniedPage />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </UnauthenticatedTemplate>
    </Box>
  );
};

export default CustomerApp;
